'use client';

import { LinkProps, Link as MuiLink, styled } from '@mui/material';
import type { ReactElement } from 'react';
import { IconArrowForward, IconLaunch } from '../Icons';

export const StyledLink = styled(MuiLink)`
  color: ${({ theme }) => (theme.vars ?? theme).palette.secondary.main};
  text-decoration-color: inherit;
  &:hover {
    color: ${({ theme }) => (theme.vars ?? theme).palette.secondary.main};
    text-decoration-thickness: 4px;
  }
  &:focus-visible {
    outline-style: dashed;
    outline-width: 2px;
    outline-offset: 2px;
    text-decoration-thickness: 3px;
  }
`;

const MenuLink = styled(MuiLink)`
  color: ${({ theme }) => (theme.vars ?? theme).palette.text.primary};
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => (theme.vars ?? theme).palette.text.primary};
    text-decoration: underline;
    text-decoration-thickness: 4px;
  }
  &:focus-visible {
    outline-style: dashed;
    outline-width: 2px;
    outline-offset: 2px;
    text-decoration: underline;
    text-decoration-thickness: 3px;
  }
`;

const IconLink = styled(StyledLink)`
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    text-decoration-thickness: 4px;
  }
  &:focus-visible {
    text-decoration: underline;
    text-decoration-thickness: 3px;
  }
`;

type LinkVariant = 'default' | 'icon' | 'menu' | 'menuIcon';

const Link = (
  props: LinkProps & {
    to?: string;
    external?: boolean;
    linkVariant?: LinkVariant;
    startIcon?: ReactElement;
    endIcon?: ReactElement;
    active?: boolean;
  },
) => {
  const {
    external = false,
    to,
    linkVariant = 'default',
    startIcon,
    endIcon,
    active = false,
    ...linkProps
  } = props;
  const href = props.href || to;
  const isExternalDomain = href && !href.includes('malarenergi.se');

  const rel =
    props.target === '_blank'
      ? isExternalDomain
        ? 'noopener noreferrer'
        : 'noopener'
      : props.rel;

  const linkComponents = {
    default: StyledLink,
    icon: IconLink,
    menu: MenuLink,
    menuIcon: MenuLink,
  };
  const Component = linkComponents[linkVariant];
  const isPhoneOrMailHref =
    typeof href === 'string' &&
    (href.toLowerCase().startsWith('tel:') ||
      href.toLowerCase().startsWith('mailto:'));
  const isMenuVariant = linkVariant === 'menu' || linkVariant === 'menuIcon';
  const activeSx = active && isMenuVariant ? { fontWeight: 700 } : {};
  const resolvedEndIcon =
    endIcon ??
    (linkVariant === 'menuIcon' ? <IconArrowForward fontSize='small' /> : null);
  return (
    <Component
      {...linkProps}
      href={href}
      rel={rel}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25em',
        ...((linkProps.sx as object) ?? {}),
        ...activeSx,
      }}
    >
      {startIcon}
      {props.children}
      {external && !isPhoneOrMailHref ? (
        <IconLaunch sx={{ height: '1ex' }} />
      ) : null}
      {resolvedEndIcon}
    </Component>
  );
};

export default Link;
